import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Check } from 'lucide-react';
import CountUp from '../../components/CountUp';

const platforms = ['Disney+', 'Hulu', 'Peacock', 'Paramount+', 'Tubi', 'Samsung TV+', 'Pluto TV'];

const reachStats = [
  { value: '40M+', label: 'CTV households accessible across all inventory' },
  { value: '100%', label: 'Brand-safe, premium streaming environments only' },
  { value: '7+', label: 'Major streaming platforms in a single campaign' }
];

const forAgency = [
  'No adjacency to misinformation or harmful content',
  'Vertical-specific safety controls per client',
  'Compliance with IAB brand safety standards',
  'Every placement reviewed against content categories',
  "Your agency's reputation is never at risk"
];

const forPerformance = [
  'Premium environments drive higher completion rates',
  'Attentive audiences on lean-back viewing sessions',
  'Higher frequency in fewer, better placements',
  'Platform relationship management included',
  'No made-for-advertising (MFA) inventory'
];

const CastInventoryPage = () => {
  return (
    <div className="min-h-screen" data-testid="cast-inventory-page">
      <Helmet><title>MarqiCast — Premium CTV Inventory</title></Helmet>

      {/* Hero */}
      <section className="py-24 md:py-32 text-center">
        <div className="container max-w-4xl">
          <h1 className="display-md text-[var(--text-primary)] mb-6" data-testid="cast-inventory-hero-title">
            Your Clients Run Where
            <br /><span className="text-[var(--accent-primary)]">Audiences Actually Watch.</span>
          </h1>
          <p className="body-lg mb-12 max-w-3xl mx-auto">
            MarqiCast gives your agency access to premium, brand-safe streaming environments — the same
            inventory national brands buy, available to your SMB clients at a fraction of the barrier to
            entry.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/contact" className="btn-primary" data-testid="cast-inventory-cta-primary">
              See Inventory & Availability
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link to="/contact" className="btn-secondary">Book Strategy Session</Link>
          </div>
        </div>
      </section>

      {/* Platform strip */}
      <section className="py-24 bg-[var(--bg-secondary)]">
        <div className="container text-center">
          <h2 className="h2 text-[var(--text-primary)] mb-16">Premium Streaming Environments. Brand-Safe. Always.</h2>
          <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-8 max-w-4xl mx-auto">
            {platforms.map((p) => (
              <span key={p} className="text-3xl md:text-4xl font-bold text-[var(--text-primary)] hover:text-[var(--accent-primary)] transition-colors">{p}</span>
            ))}
            <span className="text-3xl md:text-4xl font-bold text-[var(--accent-primary)]">+ More</span>
          </div>
          <p className="text-[var(--text-muted)] mt-12">Premium environments added regularly</p>
        </div>
      </section>

      {/* Reach stats */}
      <section className="py-24">
        <div className="container">
          <h2 className="h1 text-[var(--text-primary)] text-center mb-16 max-w-3xl mx-auto">The Scale Your Clients Need. The Quality They Deserve.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {reachStats.map((s, i) => (
              <div key={s.label} className="text-center animate-fade-in" style={{ animationDelay: `${i * 0.1}s` }}>
                <CountUp value={s.value} className="block text-6xl font-bold text-[var(--accent-primary)] mb-4" />
                <p className="body-md max-w-xs mx-auto">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand safety */}
      <section className="py-24 bg-[var(--bg-secondary)]">
        <div className="container">
          <h2 className="h1 text-[var(--text-primary)] text-center mb-16 max-w-3xl mx-auto">Brand Safety Isn't Optional. It's the Default.</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="feature-card">
              <h3 className="h3 text-[var(--accent-primary)] mb-6">For Your Agency</h3>
              <ul className="space-y-4">
                {forAgency.map((f) => (
                  <li key={f} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                    <span className="text-[var(--text-secondary)]">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="feature-card">
              <h3 className="h3 text-[var(--accent-primary)] mb-6">For Campaign Performance</h3>
              <ul className="space-y-4">
                {forPerformance.map((f) => (
                  <li key={f} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                    <span className="text-[var(--text-secondary)]">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container">
          <div className="feature-card text-center max-w-4xl mx-auto">
            <h2 className="h1 text-[var(--text-primary)] mb-6">Access the Same Inventory National Brands Buy.</h2>
            <p className="body-lg mb-4 max-w-2xl mx-auto">Your SMB clients can now advertise alongside premium content — not just on the leftover inventory other platforms sell.</p>
            <p className="body-md mb-12 max-w-2xl mx-auto">Book a session to see available inventory for your client's market.</p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact" className="btn-primary glow-effect">Check Inventory for Your Market<ArrowRight className="ml-2 w-5 h-5" /></Link>
              <Link to="/cast" className="btn-secondary">Back to MarqiCast</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CastInventoryPage;
